import { useEffect, useState } from "react";
import { Spinner } from "./Shared";

const MONO = "'JetBrains Mono', monospace";

// Read-only view of a message exactly as it will be sent. The content always
// comes from the backend, rendered by the same function that composes the real
// email — substituting the placeholders here instead would be a second
// implementation, and a preview that can disagree with what arrives is worse
// than none.
//
// Deliberately plain text in a monospace block: _smtp_send attaches
// MIMEText(body, "plain"), so dressing it up as styled HTML would show
// something the recipient never gets.
export default function MessagePreview({ fetcher, deps = [], title = "Message preview", emptyHint }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState(s => ({ ...s, loading: true }));
    Promise.resolve()
      .then(fetcher)
      .then(data => { if (!cancelled) setState({ loading: false, data, error: null }); })
      .catch(err => { if (!cancelled) setState({ loading: false, data: null, error: err.message }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const frame = children => (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
      <div style={{
        padding: "6px 12px", background: "#f3f4f6", borderBottom: "1px solid #e5e7eb",
        fontSize: 11, fontWeight: 700, color: "#4b5563", textTransform: "uppercase", letterSpacing: "0.06em",
      }}>
        {title}
      </div>
      {children}
    </div>
  );

  if (state.loading) {
    return frame(<div style={{ padding: "16px 12px", fontSize: 13, color: "#6b7280" }}><Spinner /> Rendering…</div>);
  }
  if (state.error) {
    return frame(
      <div role="alert" style={{ padding: "12px", fontSize: 13, color: "#b91c1c" }}>
        {state.error}
        {emptyHint && <div style={{ color: "#6b7280", marginTop: 4 }}>{emptyHint}</div>}
      </div>
    );
  }

  const { from_addr, reply_to, to, subject, body, note } = state.data;
  const Line = ({ label, value }) => (
    <div style={{ display: "flex", gap: 8, fontSize: 12, padding: "2px 0" }}>
      <span style={{ color: "#6b7280", minWidth: 56, flexShrink: 0 }}>{label}</span>
      <span style={{ fontFamily: MONO, color: "#1a1a1a", wordBreak: "break-all" }}>
        {value || <span style={{ color: "#6b7280", fontFamily: "inherit" }}>—</span>}
      </span>
    </div>
  );

  return frame(
    <>
      <div style={{ padding: "10px 12px", borderBottom: "1px solid #f3f4f6", background: "#fafafa" }}>
        <Line label="From" value={from_addr} />
        {/* Only when set: without one the mail carries no Reply-To header at
            all, and a "—" line would suggest an empty one. */}
        {reply_to && <Line label="Reply-To" value={reply_to} />}
        <Line label="To" value={to} />
        <Line label="Subject" value={subject} />
      </div>
      <pre style={{
        margin: 0, padding: "12px", fontFamily: MONO, fontSize: 12, lineHeight: 1.55,
        color: "#1a1a1a", whiteSpace: "pre-wrap", wordBreak: "break-word",
        maxHeight: 260, overflowY: "auto",
      }}>{body}</pre>
      {note && (
        <div style={{ padding: "8px 12px", borderTop: "1px solid #f3f4f6", fontSize: 11, color: "#6b7280", background: "#fafafa" }}>
          {note}
        </div>
      )}
    </>
  );
}
